<script setup lang="ts">
import { getMyPassageList, getPassageDetail } from '~/apis/data'
import type { FolderData, PassageData } from '~/apis/data/type'
import {useCache} from "~/common/useCache";

const app = useAppStore()
interface PassageItem {
  id: string
  type: 'folder' | 'file'
  title: string
  date: string
  passageCount?: number
  originalText?: string
  label?: string
  folderId?: number
}

interface Props {
  folderId?: number
  folderName?: string
  passageCount?: number
}

interface Emits {
  (e: 'close'): void
  (e: 'back'): void
}

const props = withDefaults(defineProps<Props>(), {
  folderId: undefined,
  passageCount: 0,
})

const emit = defineEmits<Emits>()
const dataLayerPopup = useDataLayerPopup()

const folderName = computed(() => {
  return props.folderName || dataLayerPopup.state.value.folderDetail.folderName
})

const searchKeyword = ref('')
const sortOption = ref('date_desc')
const selectedItems = ref<string[]>([])
const items = ref<PassageItem[]>([])
const currentPage = ref(1)
const totalCount = ref(0)
const pageSize = ref(10)
const pageVisible = ref(false)

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

const visiblePages = computed(() => {
  const pages: number[] = []
  const start = Math.max(1, currentPage.value - 2)
  const end = Math.min(totalPages.value, start + 4)

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
})

const fetchFolderContent = async () => {
  try {
    const sortCol = sortOption.value === 'title_asc' ? '2' : '1'
    const sortType = sortOption.value.includes('desc') ? 'desc' : 'asc'

    const response = await getMyPassageList({
      UM_ID: app.user.UM_ID,
      FOLDER_ID: props.folderId?.toString() || '',
      SEARCH_WORD: searchKeyword.value,
      CATEGORY_ID: '002',
      SORT_COL: sortCol,
      SORT_TYPE: sortType,
      CURRENTPAGE: currentPage.value,
      PAGECOUNT: pageSize.value,
    })

    if (response.data) {
      const { folder, data: passages, totalcount } = response.data

      const folderItems: PassageItem[] = (folder || []).map((item: any) => ({
        id: `folder_${item.folder_id}`,
        type: 'folder' as const,
        title: item.folder_name,
        date: '',
        passageCount: item.passage_cnt,
        folderId: item.folder_id,
      }))

      const passageItems: PassageItem[] = (passages || []).map((item: any) => ({
        id: `passage_${item.edp_idx}`,
        type: 'file' as const,
        title: item.edp_title,
        date: item.reg_date,
        label: item.passage_type_name === '변형지문' ? '변형지문' : undefined,
        originalText:
          item.passage_type_name === '일반지문' ? undefined : '원문 정보 없음',
      }))

      items.value = [...folderItems, ...passageItems]
      totalCount.value = totalcount || 0

      // 이미 선택된 지문들의 ID를 유지하기
      const savedPassageLoadData =
        dataLayerPopup.state.value.savedPassageLoad.data
      if (savedPassageLoadData?.selectedPassages) {
        const selectedPassageIds = savedPassageLoadData.selectedPassages
          .filter((passage: any) => passage.edp_idx)
          .map((passage: any) => `passage_${passage.edp_idx}`)
        // 기존 선택 항목 중 현재 페이지에 있는 항목만 유지
        selectedItems.value = selectedItems.value.filter(
          (id) =>
            selectedPassageIds.includes(id) ||
            items.value.some((item) => item.id === id),
        )
      }
      pageVisible.value = items.value.length > 0 && totalCount.value > pageSize.value
    }
  } catch (error) {
    console.error('Failed to fetch folder content:', error)
  }
}

onMounted(() => {
  fetchFolderContent()

  // 이미 선택된 지문들의 ID를 체크하기
  const savedPassageLoadData = dataLayerPopup.state.value.savedPassageLoad.data
  if (savedPassageLoadData?.selectedPassages) {
    const selectedPassageIds = savedPassageLoadData.selectedPassages
      .filter((passage: any) => passage.edp_idx)
      .map((passage: any) => `passage_${passage.edp_idx}`)
    selectedItems.value = selectedPassageIds
  }
})

const handleCloseLayer = () => {
  dataLayerPopup.hideFolderDetail()
}

const handleBackButtonClick = () => {
  emit('back')
  dataLayerPopup.hideFolderDetail()
}

const handleSearchInputChange = () => {
  // 실시간 검색 로직은 debounce 처리가 필요할 수 있음
}

const handleSearchButtonClick = () => {
  currentPage.value = 1
  fetchFolderContent()
}

const handleSortOptionChange = () => {
  currentPage.value = 1
  fetchFolderContent()
}

const handleItemSelectionChange = () => {
  // selectedItems가 reactive하므로 자동으로 업데이트됨
}

const handlePreviousPageClick = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchFolderContent()
  }
}

const handleNextPageClick = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    fetchFolderContent()
  }
}

const handlePageClick = (page: number) => {
  currentPage.value = page
  fetchFolderContent()
}

const handleAddSelectedItemsClick = async () => {
  if (selectedItems.value.length > 0) {
    const callback = dataLayerPopup.state.value.savedPassageLoad.callback
    if (callback) {
      // 이미 선택된 지문들의 ID 가져오기
      const savedPassageLoadData =
        dataLayerPopup.state.value.savedPassageLoad.data
      const existingPassageIds =
        savedPassageLoadData?.selectedPassages?.map(
          (passage: any) => `passage_${passage.edp_idx}`,
        ) || []

      // 선택된 파일 타입 항목들만 필터링 (폴더는 제외하고, 이미 선택된 지문도 제외)
      const fileItems = selectedItems.value
        .filter((itemId) => !existingPassageIds.includes(itemId))
        .map((itemId) => items.value.find((i) => i.id === itemId))
        .filter((item) => item && item.type === 'file')

      if (fileItems.length > 0) {
        try {
          // 각 지문의 상세 정보를 API로 가져오기
          const passagePromises = fileItems.map(async (item) => {
            const edpIdx = item!.id.replace('passage_', '')
            const response = await getPassageDetail({
              UM_ID: app.user.UM_ID,
              EDP_IDX: edpIdx,
            })

            // 모든 지문 정보를 포함해서 반환
            const originalContent = response.data.data.passage
            const processedContent =
              response.data.data.creturn === 'N'
                ? originalContent.replace(/\n/g, ' ')
                : originalContent

            return {
              title: response.data.data.edp_title,
              content: processedContent,
              originalContent: originalContent,
              edp_idx: response.data.data.edp_idx,
              reg_date: response.data.data.reg_date,
              passage_type: response.data.data.passage_type,
              passage_type_name: response.data.data.passage_type_name,
              passage_kr: response.data.data.passage_kr,
              creturn: response.data.data.creturn,
              creturn_name: response.data.data.creturn_name,
            }
          })

          const passages = await Promise.all(passagePromises)
          callback(passages)
        } catch (error) {
          console.error('Failed to fetch passage details:', error)
          // 에러 발생 시 제목만으로 fallback
          const passages = fileItems.map((item) => ({
            title: item!.title,
            content: `${item!.title} 지문 내용을 불러올 수 없습니다.`,
            originalContent: `${item!.title} 지문 내용을 불러올 수 없습니다.`,
          }))
          callback(passages)
        }
      }
    }

    // 폴더 상세 팝업과 부모 팝업(저장 지문 불러오기) 모두 닫기
    dataLayerPopup.hideFolderDetail()
    dataLayerPopup.hideSavedPassageLoad()
  }
}
</script>
<template>
  <div class="layer active">
    <div class="mask" @click="handleCloseLayer"></div>
    <div class="inner">
      <section class="sample fullHeight">
        <header>
          <a class="layerBack" @click="handleBackButtonClick">뒤로</a>
          <h2>{{ folderName }}</h2>
          <a class="layerClose" @click="handleCloseLayer">닫기</a>
        </header>
        <main :style="{ overflow: items.length === 0 ? 'hidden' : 'auto' }">
          <div class="popDataNone" v-if="items.length === 0">
            <p>자료가 없습니다</p>
          </div>
          <div class="searchArea" v-if="items.length > 0">
            <div class="search">
              <input
                type="text"
                placeholder="자료 제목 검색"
                v-model="searchKeyword"
                @input="handleSearchInputChange"
              />
              <a class="btnSearch" @click="handleSearchButtonClick">검색</a>
            </div>
            <select v-model="sortOption" @change="handleSortOptionChange">
              <option value="date_desc">생성일시 ↓</option>
              <option value="date_asc">생성일시 ↑</option>
              <option value="title_asc">제목 순</option>
            </select>
          </div>

          <div class="passageLoadList" v-if="items.length > 0">
            <ul>
              <li v-for="item in items" :key="item.id">
                <label class="label">
                  <span class="img">
                    <img src="/assets/images/board/img_file.svg" alt="파일" />
                  </span>
                  <span class="wrap">
                    <span class="tit">
                      {{ item.title }}
                      <span v-if="item.label" class="spanlabel">{{
                        item.label
                      }}</span>
                    </span>
                    <span class="ul">
                      <span class="li">{{ item.date }}</span>
                      <span class="li" v-if="item.originalText"
                        >원문 : {{ item.originalText }}</span
                      >
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    v-model="selectedItems"
                    :value="item.id"
                    @change="handleItemSelectionChange"
                  />
                </label>
              </li>
            </ul>
          </div>

          <div class="pagingArea" v-if="pageVisible">
            <ul>
              <li>
                <a class="btnPrev" @click="handlePreviousPageClick">이전</a>
              </li>
              <li v-for="page in visiblePages" :key="page">
                <strong v-if="page === currentPage">{{ page }}</strong>
                <a v-else @click="handlePageClick(page)">{{ page }}</a>
              </li>
              <li><a class="btnNext" @click="handleNextPageClick">다음</a></li>
            </ul>
          </div>
        </main>
        <footer>
          <div class="btnWrap">
            <a
              class="btnStyle wide"
              :class="{ disable: selectedItems.length === 0 }"
              @click="handleAddSelectedItemsClick"
            >
              {{
                selectedItems.length === 0
                  ? '지문을 선택해주세요'
                  : `${selectedItems.length}개 추가하기`
              }}
            </a>
          </div>
        </footer>
      </section>
    </div>
  </div>
</template>
